import { useEffect, useMemo, useState } from "react";
import { processImage } from "./processImage";

const validPalette = (colors) => colors?.length > 0 &&
  colors.every(([hex]) => /^[0-9a-f]{6}$/i.test(hex));

// Color edits are drafts. Trace a snapshot only on Apply, or when a new image
// brings its own palette. Superseded work must never replace the latest preview.
export function useAppliedColorPreview({ file, colors, crop, despeckle,
  simplifyCurves, smoothness, ai, originalDimensions, useSuperpixels,
  useMorphology, setSvg, setSvgPreview, setDownloadSVGLink }) {
  const [palette, setPalette] = useState({ file: null, colors: [] });
  const [completed, setCompleted] = useState(null);
  const [failure, setFailure] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (palette.file !== file || (!palette.colors.length && colors?.length)) {
      setPalette({ file, colors: colors || [] });
    }
  }, [file, colors, palette]);

  const hasUnappliedColorChanges = JSON.stringify(colors) !== JSON.stringify(palette.colors);
  const canApplyColors = validPalette(colors);
  const applyColors = (nextColors = colors) => {
    if (!validPalette(nextColors)) return;
    setPalette({ file, colors: nextColors.map((color) => [...color]) });
    setAttempt((value) => value + 1);
  };

  const signature = useMemo(() => JSON.stringify({ colors: palette.colors,
    crop, despeckle, simplifyCurves, smoothness, ai, originalDimensions,
    useSuperpixels, useMorphology }), [palette.colors, crop, despeckle,
    simplifyCurves, smoothness, ai, originalDimensions, useSuperpixels, useMorphology]);
  const ready = !!file && palette.file === file && validPalette(palette.colors);
  const matches = (result) => result?.file === file &&
    result?.signature === signature && result?.attempt === attempt;
  const isPreviewCurrent = ready && matches(completed);
  const previewError = matches(failure) ? failure.message : null;
  const previewLoading = ready && !isPreviewCurrent && !previewError;

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await processImage({ file, ...JSON.parse(signature) });
        if (cancelled) return;
        setSvg(result.svg);
        setSvgPreview(result.svgPreview);
        setDownloadSVGLink(result.downloadSVGLink);
        setCompleted({ file, signature, attempt });
        setFailure(null);
      } catch (error) {
        if (cancelled) return;
        console.error("Error processing image:", error);
        setFailure({ file, signature, attempt, message: "Could not update the preview. Try again." });
      }
    }, 1000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [ready, file, signature, attempt, setSvg, setSvgPreview, setDownloadSVGLink]);

  return { hasUnappliedColorChanges, canApplyColors, applyColors,
    previewLoading, previewError, isPreviewCurrent };
}
